import { alpha, Box, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import { WeekMenuOverflowMenu } from "./WeekMenuOverflowMenu"

type StyleProps = Readonly<{
    /** Share of ready days (0..1) shown by the pill's progress bar. */
    readyFraction: number
}>

const useStyles = makeStyles<StyleProps>()((theme, { readyFraction }) => ({
    toolbar: {
        padding: ".75rem 1rem",
        background: theme.palette.background.paper,
        borderRadius: ".75rem",
        flexWrap: "wrap",
    },
    navButton: {
        width: "2rem",
        height: "2rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
        "& svg": {
            fontSize: ".875rem",
        },
    },
    weekLabel: {
        minWidth: "12rem",
        textAlign: "center",
        fontWeight: 600,
    },
    readyPill: {
        padding: ".375rem .875rem",
        borderRadius: "1.25rem",
        background: theme.palette.tailwind.amber[100],
    },
    readyLabel: {
        color: theme.palette.tailwind.amber[800],
        whiteSpace: "nowrap",
    },
    readyTrack: {
        width: "5.25rem",
        height: ".375rem",
        borderRadius: "62.5rem",
        background: alpha(theme.palette.tailwind.amber[950], 0.2),
        overflow: "hidden",
    },
    readyFill: {
        width: `${readyFraction * 100}%`,
        height: "100%",
        borderRadius: "62.5rem",
        background: alpha(theme.palette.tailwind.amber[950], 0.3),
    },
}))

type Props = Readonly<{
    weekLabel: string
    readyDayCount: number
    dayCount: number
    onPreviousWeek: () => void
    onNextWeek: () => void
    onManageTemplates: () => void
    onCustomerPreview: () => void
    onCopyWeek: () => void
    onClearWeek: () => void
    onExportPdf: () => void
    onPrintKitchenSheet: () => void
}>

export function WeekMenuToolbar({
    weekLabel,
    readyDayCount,
    dayCount,
    onPreviousWeek,
    onNextWeek,
    onManageTemplates,
    onCustomerPreview,
    onCopyWeek,
    onClearWeek,
    onExportPdf,
    onPrintKitchenSheet,
}: Props) {
    const { classes } = useStyles({ readyFraction: dayCount > 0 ? readyDayCount / dayCount : 0 })

    return (
        <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.toolbar}>
            <Stack direction="row" alignItems="center" spacing="1rem">
                <LocalizedStrict id="merchant-week-menu-prev-week-button" attrs={{ "aria-label": true }}>
                    <IconButton aria-label="Previous week" className={classes.navButton} onClick={onPreviousWeek}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </IconButton>
                </LocalizedStrict>
                <Typography variant="body1" className={classes.weekLabel}>
                    {weekLabel}
                </Typography>
                <LocalizedStrict id="merchant-week-menu-next-week-button" attrs={{ "aria-label": true }}>
                    <IconButton aria-label="Next week" className={classes.navButton} onClick={onNextWeek}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </IconButton>
                </LocalizedStrict>
            </Stack>
            <Stack direction="row" alignItems="center" spacing="1rem" className={classes.readyPill}>
                <LocalizedStrict
                    id="merchant-week-menu-days-ready-label"
                    vars={{ ready: readyDayCount, total: dayCount }}
                >
                    <Typography variant="subtitle2" className={classes.readyLabel}>
                        {readyDayCount}/{dayCount} Days ready
                    </Typography>
                </LocalizedStrict>
                <Box className={classes.readyTrack}>
                    <Box className={classes.readyFill} />
                </Box>
            </Stack>
            <Stack direction="row" flexGrow={1} justifyContent="flex-end">
                <WeekMenuOverflowMenu
                    onCopyWeek={onCopyWeek}
                    onClearWeek={onClearWeek}
                    onManageTemplates={onManageTemplates}
                    onCustomerPreview={onCustomerPreview}
                    onExportPdf={onExportPdf}
                    onPrintKitchenSheet={onPrintKitchenSheet}
                />
            </Stack>
        </Stack>
    )
}
